// MobileNavDrawer.jsx — bottom-sheet drawer for mobile nav.
// v11 (2026-02-17) — Iter41: single mobile nav system.
//   - Adds `orphans` section (tabs allowed for the role but absent from
//     TAB_GROUPS), mirroring the deleted MobileMenuSheet coverage.
//   - Reacts to `tw-open-mob-drawer` window event so MobileBottomBar's
//     ≡ button opens THIS drawer (no second menu component exists).
//   - Version footer reads APP_VERSION (auto-generated per build); no
//     literal version strings allowed (guarded by
//     test_no_literal_version_in_mobile_drawer.py).
import React, { useEffect, useMemo, useState } from "react";
import { TAB_GROUPS } from "./tabs";
import { APP_VERSION } from "./lib/version";

export default function MobileNavDrawer({ tab, setTab, tabsForRole, byId, badges = {} }) {
  const [open, setOpen] = useState(false);
  // Track which group is expanded. Only ONE group expanded at a time so the
  // list stays short (user complaint Feb 2026: "القائمة طويلة مرة").
  // Default: open the group containing the current tab so the user can see
  // where they are; everything else is collapsed.
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  // v11 (2026-02-17) — listen to global "open drawer" event so the ≡
  // button in MobileBottomBar can trigger this same component (single
  // mobile nav system, no second menu).
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("tw-open-mob-drawer", onOpen);
    return () => window.removeEventListener("tw-open-mob-drawer", onOpen);
  }, []);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Close when route changes
  const handleSelect = (id) => {
    setTab(id);
    setOpen(false);
  };

  // v200.50 (2026-08-14) — `hidden:true` tabs are excluded from the
  // mobile drawer. Deep links still work; only the visible entry
  // disappears. Guarded by test_nav_hidden_flag_enforced.py.
  const allowed = new Set(tabsForRole.filter((t) => !t.hidden).map((t) => t.id));
  const groups = useMemo(() => {
    const built = TAB_GROUPS
      .map((g) => {
        const primaryAllowed = allowed.has(g.primary);
        const children = g.children.filter((c) => allowed.has(c));
        return primaryAllowed || children.length > 0 ? { ...g, children, primaryAllowed } : null;
      })
      .filter(Boolean);
    // v11 (2026-02-17) — orphans coverage. Any tab visible to this role
    // but absent from TAB_GROUPS (and not hidden) surfaces under "أخرى",
    // matching what the removed MobileMenuSheet used to expose. Guarded
    // by test_mobile_drawer_orphan_coverage.py.
    const covered = new Set();
    for (const g of built) {
      if (g.primaryAllowed) covered.add(g.primary);
      for (const c of g.children) covered.add(c);
    }
    const orphans = tabsForRole
      .filter((t) => !t.hidden && !covered.has(t.id))
      .map((t) => t.id);
    if (orphans.length) {
      built.push({ id: "orphans", label: "أخرى", primary: null,
                   children: orphans, primaryAllowed: false });
    }
    return built;
  }, [tabsForRole]);

  const currentLabel = byId[tab]?.label || "الرئيسية";
  // Find which group contains the active tab — used to highlight in drawer
  const activeGroup = TAB_GROUPS.find((g) => g.primary === tab || g.children.includes(tab));

  // When the drawer opens, auto-expand the group that contains the current
  // tab so the user lands on context. All others stay collapsed.
  useEffect(() => {
    if (open && activeGroup) setExpandedGroup(activeGroup.id);
  }, [open, activeGroup]);

  const toggleGroup = (gid) => setExpandedGroup((cur) => (cur === gid ? null : gid));

  return (
    <>
      {/* Sticky mobile bar — shows current page + menu button */}
      <div className="mob-nav" data-testid="mob-nav">
        <button
          type="button"
          className="mob-nav-btn"
          onClick={() => setOpen(true)}
          aria-label="فتح القائمة"
          data-testid="mob-nav-open"
        >
          <span className="mob-nav-icon" aria-hidden>
            <span /><span /><span />
          </span>
        </button>
        <div className="mob-nav-current">
          {activeGroup && activeGroup.label !== currentLabel && (
            <span className="mob-nav-group">{activeGroup.label}</span>
          )}
          <span className="mob-nav-title" data-testid="mob-nav-title">{currentLabel}</span>
        </div>
        <div className="mob-nav-spacer" />
      </div>

      {/* Drawer overlay */}
      {open && (
        <>
          <div className="mob-drawer-backdrop" onClick={() => setOpen(false)} data-testid="mob-drawer-backdrop" />
          <aside className="mob-drawer" role="dialog" aria-label="قائمة التنقّل" data-testid="mob-drawer">
            <header className="mob-drawer-head">
              <img src="/icon-96.png" alt="ثواني" className="mob-drawer-logo" />
              <div>
                <div className="mob-drawer-brand">ثواني</div>
                <div className="mob-drawer-sub">لوحة تحكم المقهى</div>
              </div>
              <button
                type="button"
                className="mob-drawer-close"
                onClick={() => setOpen(false)}
                aria-label="إغلاق"
                data-testid="mob-drawer-close"
              >×</button>
            </header>

            <nav className="mob-drawer-nav">
              {groups.map((g) => {
                const isActiveGroup = tab === g.primary || g.children.includes(tab);
                const primaryT = byId[g.primary];
                const hasChildren = g.children.length > 0;
                const isExpanded = expandedGroup === g.id;
                return (
                  <section key={g.id} className={`mob-drawer-section ${isActiveGroup ? "is-active" : ""}`}>
                    {primaryT && g.primaryAllowed ? (
                      <div style={{ display: "flex", alignItems: "stretch", width: "100%" }}>
                        <button
                          type="button"
                          className={`mob-drawer-item mob-drawer-primary ${tab === g.primary ? "on" : ""}`}
                          onClick={() => handleSelect(g.primary)}
                          data-testid={`mob-drawer-${g.primary}`}
                          style={{ flex: 1 }}
                        >
                          <span className="mob-drawer-label">{primaryT.label}</span>
                          {tab === g.primary && <span className="mob-drawer-dot" aria-hidden />}
                        </button>
                        {hasChildren && (
                          <button
                            type="button"
                            onClick={() => toggleGroup(g.id)}
                            aria-label={isExpanded ? "طيّ القائمة الفرعية" : "فتح القائمة الفرعية"}
                            aria-expanded={isExpanded}
                            data-testid={`mob-drawer-expand-${g.id}`}
                            style={{
                              all: "unset",
                              cursor: "pointer",
                              padding: "8px 14px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "var(--brown-deep)",
                              fontSize: 14,
                              opacity: 0.75,
                              borderInlineStart: "1px solid var(--border)",
                              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          >▶</button>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => hasChildren && toggleGroup(g.id)}
                        className="mob-drawer-section-h"
                        data-testid={`mob-drawer-section-${g.id}`}
                        style={{
                          all: "unset",
                          cursor: hasChildren ? "pointer" : "default",
                          width: "100%",
                          boxSizing: "border-box",
                          padding: "10px 14px",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontWeight: 700,
                          fontSize: 13,
                          color: "var(--brown-deep)",
                          background: isExpanded ? "rgba(212,175,55,0.06)" : "transparent",
                        }}
                      >
                        {hasChildren && (
                          <span
                            style={{
                              fontSize: 11,
                              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              opacity: 0.7,
                            }}
                            aria-hidden
                          >▶</span>
                        )}
                        <span>{g.label}</span>
                        {hasChildren && (
                          <span style={{ marginInlineStart: "auto", fontSize: 10.5, color: "var(--muted)", fontWeight: 500 }}>
                            {g.children.length}
                          </span>
                        )}
                      </button>
                    )}
                    {hasChildren && isExpanded && (
                      <div className="mob-drawer-children">
                          {g.children.map((cid) => {
                          const ct = byId[cid];
                          if (!ct) return null;
                          const badge = badges[cid] || 0;
                          return (
                            <button
                              key={cid}
                              type="button"
                              className={`mob-drawer-item mob-drawer-child ${tab === cid ? "on" : ""}`}
                              onClick={() => handleSelect(cid)}
                              data-testid={`mob-drawer-${cid}`}
                            >
                              <span className="mob-drawer-label">{ct.label}</span>
                              {badge > 0 && (
                                <span style={{
                                  background: "#dc2626", color: "#fff", borderRadius: 999,
                                  fontSize: 10, padding: "1px 7px", fontWeight: 800,
                                  marginInlineStart: 6,
                                }}>{badge > 99 ? "99+" : badge}</span>
                              )}
                              {tab === cid && <span className="mob-drawer-dot" aria-hidden />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </nav>

            <footer className="mob-drawer-foot">
              <span>تطبيق ثواني · {APP_VERSION}</span>
            </footer>
          </aside>
        </>
      )}
    </>
  );
}
